import BigNumber from 'bignumber.js'
import { YEAR_IN_DAYS, YEAR_IN_SECONDS } from 'config/constants/time'
import { getBalanceNumber } from './formatBalance'

const isFiniteNumber = (value: BigNumber): boolean => !value.isNaN() && value.isFinite()

/**
 * Get the APR value in %
 * @param stakingTokenPrice Token price in the same quote currency
 * @param rewardTokenPrice Token price in the same quote currency
 * @param totalStaked Total amount of stakingToken in the launchpool
 * @param tokensPerSec Amount of tokens allocated to the pool for each sec
 * @returns Null if the APR is NaN or infinite.
 */
export const getPoolApr = (
  stakingTokenPrice: number,
  rewardTokenPrice: number,
  totalStaked: number,
  tokensPerSec: BigNumber,
): number => {
  const totalRewardPerYear = new BigNumber(rewardTokenPrice).times(tokensPerSec).times(YEAR_IN_SECONDS)
  const totalStakedInPool = new BigNumber(stakingTokenPrice).times(totalStaked)
  const apr = totalRewardPerYear.div(totalStakedInPool).times(100)
  return isFiniteNumber(apr) ? apr.toNumber() : 0
}

/**
 * Get farm APR value in %
 * @param tokensPerSec allocationPoint / totalAllocationPoint
 * @param rewardTokenPrice Reward token price in USD
 * @param totalLiquidity Total pool liquidity in USD
 * @returns Farm Apr
 */
export const getFarmApr = (tokensPerSec: BigNumber, rewardTokenPrice: BigNumber, totalLiquidity: BigNumber): number => {
  const yearlyNmxRewardAllocation = tokensPerSec ? tokensPerSec.times(YEAR_IN_SECONDS) : new BigNumber(NaN)
  const apr = yearlyNmxRewardAllocation.times(rewardTokenPrice).div(totalLiquidity).times(100)
  return isFiniteNumber(apr) ? apr.toNumber() : 0
}

/**
 * Get holder bonus APR value in %
 * @param dailyRewards Daily rewards
 * @param rewardTokenPrice Reward token price in USD
 * @param totalStaked Total staked in the pool in USD
 * @returns Holder bonus Apr
 */
export const getHolderBonusApr = (
  dailyRewards: BigNumber,
  rewardTokenPrice: BigNumber,
  totalStaked: BigNumber,
): number => {
  const apr = dailyRewards.times(rewardTokenPrice).times(YEAR_IN_DAYS).div(totalStaked).times(100)
  return isFiniteNumber(apr) ? apr.toNumber() : 0
}

/**
 * Get LP rewards APR value in %
 * @param dailyReceivedFees Total value of LP fees received in the pool
 * @param totalLiquidity Total pool liquidity in USD
 * @returns LP rewards Apr
 */
export const getLpRewardsApr = (dailyReceivedFees: BigNumber, totalLiquidity: BigNumber): number => {
  const apr = dailyReceivedFees.times(YEAR_IN_DAYS).div(totalLiquidity).times(100)
  return isFiniteNumber(apr) ? apr.toNumber() : 0
}

export const getMainPoolApr = (nmxPrice, totalStaked, tokensPerSec) =>
  getPoolApr(nmxPrice, nmxPrice, getBalanceNumber(new BigNumber(totalStaked)), tokensPerSec)
